import _ from 'underscore';
import { useCallback, useContext, useMemo } from 'react';
import useSwr from 'swr';
import getClientCenterAlerts from '@bingads-webui-campaign-legacy/alert/model/client-center';
import * as scopes from '@bingads-webui-campaign-legacy/alert/model/scopes';
import { PreferenceContext } from '@bingads-webui-react/preference-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import getCouponStatus from '@bingads-webui-campaign-legacy/dashboard-cards/welcome-tips-card/coupon-status';
import { TIP_ID } from '@bingads-webui-campaign-react/overview-welcome-tips';

import { useDashboardPreferences } from './use-dashboard-preferences';

const logApi = 'tips-card/index';
const couponStatusUpfrontSpending = 249;
const couponStatusActiveCoupon = 250;

export function useWelcomeTipPages({
  createdCampaign,
  skippedBothAtAccountCreation,
  skippedNoneAtAccountCreation,
  navigationLevel,
  perfMarkerUtils,
}) {
  const instrumentation = useContext(InstrumentationContext) as any;
  const { activity } = useMemo(() => instrumentation.createScenarioAndActivity({
    scenarioName: 'WelcomeTipsCard',
    activityName: 'WelcomeTipPages',
  }), [instrumentation]);

  const preference = useContext(PreferenceContext);
  const { scope } = useContext(ScopeContext) as any;
  const { aid: accountId, uid: userId, cid: customerId } = scope?.values || {};
  const { saveDashBoardOptions } = useDashboardPreferences({ preference, navigationLevel });
  const fetch = useCallback(() => {
    perfMarkerUtils?.willFetchData();
    return getClientCenterAlerts(scopes.$root)
      .then((alerts) => {
        const paymentAlert = _.filter(alerts, alert => alert.id && alert.id.indexOf('AddPaymentInstrument') !== -1);
        let hasValidPaymentMethod = false;
        let skippedNoneInFirstLoad = skippedNoneAtAccountCreation;

        if (_.isEmpty(paymentAlert)) {
          hasValidPaymentMethod = true;
        }

        let hasPaymentPermission = true;

        if (!hasValidPaymentMethod) {
          hasPaymentPermission = paymentAlert[0].id.indexOf('NoPermission') === -1;
        }

        const newOptions = {};
        if (hasValidPaymentMethod && createdCampaign) {
          // Skipped none payment and campaign creation.
          // Store it in preference so that we show different tips if user added them later
          if (skippedNoneAtAccountCreation === undefined) {
            newOptions[skippedNoneAtAccountCreation] = true;
            skippedNoneInFirstLoad = true;
          }
        } else if (skippedNoneAtAccountCreation === undefined) {
          newOptions[skippedNoneAtAccountCreation] = false;
        }

        if (!hasValidPaymentMethod && !createdCampaign) {
          // Skipped both payment and campaign creation.
          // Store it in preference so that we show different tips when user adds campaign
          if (skippedBothAtAccountCreation === undefined) {
            newOptions[skippedBothAtAccountCreation] = true;
          }
        } else if (skippedBothAtAccountCreation === undefined) {
          newOptions[skippedBothAtAccountCreation] = false;
        }
        saveDashBoardOptions(newOptions);

        let hasCoupon = false;
        const showWelcomeMsg = hasValidPaymentMethod ? skippedNoneInFirstLoad : !skippedBothAtAccountCreation;

        return getCouponStatus()
          .then((coupon) => {
            if (hasValidPaymentMethod && coupon && coupon.Redemptions) {
              const redeemedCoupons = _.filter(coupon.Redemptions, redeemed => redeemed.StatusId === couponStatusUpfrontSpending || redeemed.StatusId === couponStatusActiveCoupon);

              hasCoupon = !_.isEmpty(redeemedCoupons);
            } else if (coupon && coupon.AssociatedCoupon) {
              const rex = /\/Date\((\d+)\)\//;
              const matchResult = rex.exec(coupon.AssociatedCoupon.ExpiryDateFromAssociation);

              if (matchResult && matchResult[1]) {
                const expirationDate = parseInt(matchResult[1], 10);

                if (!_.isNaN(expirationDate) && new Date() < new Date(expirationDate)) {
                  hasCoupon = true;
                }
              }
            }

            return {
              showWelcomeMsg,
              createdCampaign,
              hasPaymentPermission,
              hasValidPaymentMethod,
              hasCoupon,
            };
          }).catch((response) => {
            // no need to log ajax error here.
            if (response.status === undefined) {
              activity.error(`${logApi} ${response.stack}`);
            }

            return {
              showWelcomeMsg,
              hasPaymentPermission,
              hasCoupon,
              createdCampaign,
              hasValidPaymentMethod,
            };
          });
      }).catch((response) => {
        // no need to log ajax error here.
        if (response.status === undefined) {
          activity.error(`${logApi} ${response.stack}`);
        }

        return {
          showWelcomeMsg: true,
          hasPaymentPermission: false,
          hasCoupon: false,
          createdCampaign,
          hasValidPaymentMethod: true,
        };
      });
  }, [accountId, activity, createdCampaign, customerId, perfMarkerUtils, saveDashBoardOptions, skippedBothAtAccountCreation, skippedNoneAtAccountCreation, userId]);

  const { isLoading, data, error } = useSwr({}, fetch);

  return useMemo(() => {
    const {
      showWelcomeMsg,
      hasPaymentPermission,
      hasCoupon,
      createdCampaign,
      hasValidPaymentMethod,
    } = data || {};
    const pages = [];
    if (hasValidPaymentMethod && createdCampaign && hasCoupon) {
      pages.push(TIP_ID.PROMO);
    }
    if (!createdCampaign) {
      pages.push(TIP_ID.CREATE_CAMPAIGN);
    }
    if (!hasValidPaymentMethod) {
      pages.push(TIP_ID.ADD_PAYMENT);
    }
    if (hasValidPaymentMethod && createdCampaign) {
      pages.push(TIP_ID.COACHING);
      pages.push(TIP_ID.HELP);
    }

    return {
      isLoading,
      error,
      pages,
      hasPaymentPermission,
      showWelcomeMsg,
      isPaymentFirstTile: createdCampaign && !hasValidPaymentMethod,
      hasCoupon,
    };
  }, [isLoading, error, data]);
}
